<script setup>
import { ref, onMounted, nextTick } from 'vue'
import { statisticsService } from '@/services/statisticsService'
import { useLanguageStore } from '@/pinia'
import { Chart, registerables } from 'chart.js'
import { useToast } from '@/composables/useToast'

Chart.register(...registerables)

const toast = useToast()

const languageStore = useLanguageStore()
const emits = defineEmits(['set_title'])

const statTypes = [
  { value: 'DAILY', label: languageStore.get('daily') },
  { value: 'WEEKLY', label: languageStore.get('weekly') },
  { value: 'MONTHLY', label: languageStore.get('monthly') },
  { value: 'TOTAL', label: languageStore.get('all') }
]

const selectedStatType = ref('DAILY')
const selectedDate = ref(getYesterdayString())
const selectedMonth = ref(getCurrentMonth())
const selectedWeek = ref(getTodayString())
const statistics = ref(null)
const loading = ref(false)
const chartCanvas = ref(null)
let chartInstance = null

function getTodayString() {
  const today = new Date()
  const year = today.getFullYear()
  const month = String(today.getMonth() + 1).padStart(2, '0')
  const day = String(today.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function getYesterdayString() {
  const yesterday = new Date()
  yesterday.setDate(yesterday.getDate() - 1)
  const year = yesterday.getFullYear()
  const month = String(yesterday.getMonth() + 1).padStart(2, '0')
  const day = String(yesterday.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

// 주어진 날짜가 속한 주의 월요일을 반환
function getMondayOfWeek(dateString) {
  const date = new Date(dateString)
  const day = date.getDay()
  // 일요일(0)이면 6일 전, 그 외에는 (day - 1)일 전이 월요일
  const diff = day === 0 ? 6 : day - 1
  date.setDate(date.getDate() - diff)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const dayOfMonth = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${dayOfMonth}`
}

function getCurrentMonth() {
  const today = new Date()
  const year = today.getFullYear()
  const month = String(today.getMonth() + 1).padStart(2, '0')
  return `${year}-${month}`
}

const selectStatType = (type) => {
  selectedStatType.value = type
  // 타입 변경 시 날짜 초기화
  if (type === 'DAILY') {
    selectedDate.value = getYesterdayString()
  } else if (type === 'WEEKLY') {
    selectedWeek.value = getTodayString()
  } else if (type === 'MONTHLY') {
    selectedMonth.value = getCurrentMonth()
  }
  fetchStatistics()
}

const fetchStatistics = async () => {
  loading.value = true
  try {
    let response
    if (selectedStatType.value === 'TOTAL') {
      // 전체: 모든 일일 데이터를 실시간으로 합산
      response = await statisticsService.getRecentStatistics('DAILY', 9999)
      statistics.value = sumStatistics(response.data)
    } else if (selectedStatType.value === 'DAILY') {
      response = await statisticsService.getDailyStatistics(selectedDate.value)
      statistics.value = response.data
    } else if (selectedStatType.value === 'WEEKLY') {
      // 주간: 일일 데이터를 실시간으로 합산
      const mondayDate = getMondayOfWeek(selectedWeek.value)
      const sundayDate = getSundayOfWeek(selectedWeek.value)
      response = await statisticsService.getStatisticsByDateRange('DAILY', mondayDate, sundayDate)
      statistics.value = sumStatistics(response.data)
    } else if (selectedStatType.value === 'MONTHLY') {
      // 월간: 일일 데이터를 실시간으로 합산
      const monthStart = selectedMonth.value + '-01'
      const monthEnd = getLastDayOfMonth(selectedMonth.value)
      response = await statisticsService.getStatisticsByDateRange('DAILY', monthStart, monthEnd)
      statistics.value = sumStatistics(response.data)
    }
  } catch (error) {
    // 404는 데이터 없음 (에러 아님)
    if (error.response && error.response.status === 404) {
      statistics.value = null
    } else {
      console.error('Failed to fetch statistics:', error)
      toast.error(languageStore.get('statisticsLoadFailed'))
      statistics.value = null
    }
  } finally {
    loading.value = false
  }
  // loading이 false가 된 후 canvas가 DOM에 렌더링되면 차트 그리기
  if (statistics.value) {
    await fetchChartData()
  }
}

// 일일 데이터 배열을 합산
function sumStatistics(dataArray) {
  if (!dataArray || dataArray.length === 0) {
    return null
  }
  return {
    plenaryMeetingCount: dataArray.reduce((sum, d) => sum + (d.plenaryMeetingCount || 0), 0),
    committeeMeetingCount: dataArray.reduce((sum, d) => sum + (d.committeeMeetingCount || 0), 0),
    totalMeetingCount: dataArray.reduce((sum, d) => sum + (d.totalMeetingCount || 0), 0),
    videoUploadCount: dataArray.reduce((sum, d) => sum + (d.videoUploadCount || 0), 0),
    liveBroadcastCount: dataArray.reduce((sum, d) => sum + (d.liveBroadcastCount || 0), 0),
    snsUploadCount: dataArray.reduce((sum, d) => sum + (d.snsUploadCount || 0), 0),
    ftpTransferCount: dataArray.reduce((sum, d) => sum + (d.ftpTransferCount || 0), 0),
    youtubeUploadCount: dataArray.reduce((sum, d) => sum + (d.youtubeUploadCount || 0), 0),
    facebookUploadCount: dataArray.reduce((sum, d) => sum + (d.facebookUploadCount || 0), 0),
    twitterUploadCount: dataArray.reduce((sum, d) => sum + (d.twitterUploadCount || 0), 0)
  }
}

const generateStatistics = async () => {
  if (!confirm(languageStore.get('confirmGenerateStatistics'))) return

  loading.value = true
  try {
    if (selectedStatType.value === 'DAILY') {
      await statisticsService.generateDailyStatistics(selectedDate.value)
    } else if (selectedStatType.value === 'WEEKLY') {
      // 선택한 날짜가 속한 주의 월요일로 변환
      const mondayDate = getMondayOfWeek(selectedWeek.value)
      await statisticsService.generateWeeklyStatistics(mondayDate)
    } else if (selectedStatType.value === 'MONTHLY') {
      const monthStartDate = selectedMonth.value + '-01'
      await statisticsService.generateMonthlyStatistics(monthStartDate)
    } else if (selectedStatType.value === 'TOTAL') {
      await statisticsService.generateTotalStatistics()
    }

    toast.success(languageStore.get('statisticsGeneratedSuccess'))
    await fetchStatistics()
  } catch (error) {
    console.error('Failed to generate statistics:', error)
    toast.error(languageStore.get('statisticsGenerateFailed'))
  } finally {
    loading.value = false
  }
}

const fetchChartData = async () => {
  try {
    await nextTick()

    if (chartInstance) {
      chartInstance.destroy()
      chartInstance = null
    }

    if (!chartCanvas.value) return

    const ctx = chartCanvas.value.getContext('2d')

    if (selectedStatType.value === 'DAILY') {
      // 일일: 라인 차트 - 최근 30일 추이
      await renderDailyChart(ctx)
    } else if (selectedStatType.value === 'WEEKLY') {
      // 주간: 바 차트 - 해당 주의 일별 데이터 (7일)
      await renderWeeklyChart(ctx)
    } else if (selectedStatType.value === 'MONTHLY') {
      // 월간: 바 차트 - 해당 월의 일별 데이터
      await renderMonthlyChart(ctx)
    } else if (selectedStatType.value === 'TOTAL') {
      // 전체: 도넛 차트 - 항목별 비율
      renderTotalChart(ctx)
    }
  } catch (error) {
    console.error('Failed to fetch chart data:', error)
  }
}

// 일일: 라인 차트 - 최근 30일 추이
const renderDailyChart = async (ctx) => {
  const response = await statisticsService.getRecentStatistics('DAILY', 30)
  if (!response.data || response.data.length === 0) return

  const data = response.data.reverse()

  chartInstance = new Chart(ctx, {
    type: 'line',
    data: {
      labels: data.map(d => d.statDate?.substring(5) || ''),
      datasets: [
        { label: languageStore.get('plenarySession'), data: data.map(d => d.plenaryMeetingCount || 0), borderColor: '#667eea', backgroundColor: 'rgba(102, 126, 234, 0.1)', tension: 0.4 },
        { label: languageStore.get('committee'), data: data.map(d => d.committeeMeetingCount || 0), borderColor: '#f093fb', backgroundColor: 'rgba(240, 147, 251, 0.1)', tension: 0.4 },
        { label: languageStore.get('liveBroadcast'), data: data.map(d => d.liveBroadcastCount || 0), borderColor: '#ff6b6b', backgroundColor: 'rgba(255, 107, 107, 0.1)', tension: 0.4 },
        { label: languageStore.get('videoUpload'), data: data.map(d => d.videoUploadCount || 0), borderColor: '#4facfe', backgroundColor: 'rgba(79, 172, 254, 0.1)', tension: 0.4 },
        { label: languageStore.get('snsUpload'), data: data.map(d => d.snsUploadCount || 0), borderColor: '#43e97b', backgroundColor: 'rgba(67, 233, 123, 0.1)', tension: 0.4 },
        { label: languageStore.get('ftpTransfer'), data: data.map(d => d.ftpTransferCount || 0), borderColor: '#ffa726', backgroundColor: 'rgba(255, 167, 38, 0.1)', tension: 0.4 }
      ]
    },
    options: getLineChartOptions(languageStore.get('dailyStatisticsTrend'))
  })
}

// 주간: 바 차트 - 해당 주의 일별 데이터 (7일)
const renderWeeklyChart = async (ctx) => {
  const mondayDate = getMondayOfWeek(selectedWeek.value)
  const sundayDate = getSundayOfWeek(selectedWeek.value)

  const response = await statisticsService.getStatisticsByDateRange('DAILY', mondayDate, sundayDate)
  if (!response.data || response.data.length === 0) return

  const data = response.data.reverse()
  const dayLabels = [languageStore.get('mon'), languageStore.get('tue'), languageStore.get('wed'), languageStore.get('thu'), languageStore.get('fri'), languageStore.get('sat'), languageStore.get('sun')]

  // 7일 데이터 매핑 (없는 날짜는 0으로)
  const weekData = []
  const monday = new Date(mondayDate)
  for (let i = 0; i < 7; i++) {
    const targetDate = new Date(monday)
    targetDate.setDate(monday.getDate() + i)
    const dateStr = targetDate.toISOString().split('T')[0]
    const found = data.find(d => d.statDate === dateStr)
    weekData.push(found || { plenaryMeetingCount: 0, committeeMeetingCount: 0, liveBroadcastCount: 0, videoUploadCount: 0, snsUploadCount: 0, ftpTransferCount: 0 })
  }

  chartInstance = new Chart(ctx, {
    type: 'bar',
    data: {
      labels: dayLabels,
      datasets: [
        { label: languageStore.get('plenarySession'), data: weekData.map(d => d.plenaryMeetingCount || 0), backgroundColor: '#667eea' },
        { label: languageStore.get('committee'), data: weekData.map(d => d.committeeMeetingCount || 0), backgroundColor: '#f093fb' },
        { label: languageStore.get('liveBroadcast'), data: weekData.map(d => d.liveBroadcastCount || 0), backgroundColor: '#ff6b6b' },
        { label: languageStore.get('videoUpload'), data: weekData.map(d => d.videoUploadCount || 0), backgroundColor: '#4facfe' },
        { label: languageStore.get('snsUpload'), data: weekData.map(d => d.snsUploadCount || 0), backgroundColor: '#43e97b' },
        { label: languageStore.get('ftpTransfer'), data: weekData.map(d => d.ftpTransferCount || 0), backgroundColor: '#ffa726' }
      ]
    },
    options: getBarChartOptions(languageStore.get('weeklyStatisticsTrend') + ` (${mondayDate} ~ ${sundayDate})`)
  })
}

// 월간: 바 차트 - 해당 월의 일별 데이터
const renderMonthlyChart = async (ctx) => {
  const monthStart = selectedMonth.value + '-01'
  const monthEnd = getLastDayOfMonth(selectedMonth.value)

  const response = await statisticsService.getStatisticsByDateRange('DAILY', monthStart, monthEnd)
  if (!response.data || response.data.length === 0) return

  const data = response.data.reverse()

  chartInstance = new Chart(ctx, {
    type: 'bar',
    data: {
      labels: data.map(d => d.statDate?.substring(8) + languageStore.get('day')),
      datasets: [
        { label: languageStore.get('plenarySession'), data: data.map(d => d.plenaryMeetingCount || 0), backgroundColor: '#667eea' },
        { label: languageStore.get('committee'), data: data.map(d => d.committeeMeetingCount || 0), backgroundColor: '#f093fb' },
        { label: languageStore.get('liveBroadcast'), data: data.map(d => d.liveBroadcastCount || 0), backgroundColor: '#ff6b6b' },
        { label: languageStore.get('videoUpload'), data: data.map(d => d.videoUploadCount || 0), backgroundColor: '#4facfe' },
        { label: languageStore.get('snsUpload'), data: data.map(d => d.snsUploadCount || 0), backgroundColor: '#43e97b' },
        { label: languageStore.get('ftpTransfer'), data: data.map(d => d.ftpTransferCount || 0), backgroundColor: '#ffa726' }
      ]
    },
    options: getBarChartOptions(languageStore.get('monthlyStatisticsTrend') + ` (${selectedMonth.value})`)
  })
}

// 전체: 도넛 차트 - 항목별 비율
const renderTotalChart = (ctx) => {
  if (!statistics.value) return

  const labels = [
    languageStore.get('plenarySession'),
    languageStore.get('committee'),
    languageStore.get('liveBroadcast'),
    languageStore.get('videoUpload'),
    languageStore.get('snsUpload'),
    languageStore.get('ftpTransfer')
  ]
  const dataValues = [
    statistics.value.plenaryMeetingCount || 0,
    statistics.value.committeeMeetingCount || 0,
    statistics.value.liveBroadcastCount || 0,
    statistics.value.videoUploadCount || 0,
    statistics.value.snsUploadCount || 0,
    statistics.value.ftpTransferCount || 0
  ]

  chartInstance = new Chart(ctx, {
    type: 'doughnut',
    data: {
      labels: labels,
      datasets: [{
        data: dataValues,
        backgroundColor: ['#667eea', '#f093fb', '#ff6b6b', '#4facfe', '#43e97b', '#ffa726']
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: 'right', labels: { padding: 20, font: { size: 12 } } },
        title: { display: true, text: languageStore.get('totalStatisticsRatio'), font: { size: 14 }, color: '#6c757d' }
      }
    }
  })
}

// 주어진 날짜가 속한 주의 일요일을 반환
function getSundayOfWeek(dateString) {
  const date = new Date(dateString)
  const day = date.getDay()
  const diff = day === 0 ? 0 : 7 - day
  date.setDate(date.getDate() + diff)
  return date.toISOString().split('T')[0]
}

// 해당 월의 마지막 날짜 반환
function getLastDayOfMonth(yearMonth) {
  const [year, month] = yearMonth.split('-').map(Number)
  const lastDay = new Date(year, month, 0).getDate()
  return `${yearMonth}-${String(lastDay).padStart(2, '0')}`
}

// 라인 차트 옵션
function getLineChartOptions(title) {
  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top', labels: { usePointStyle: true, padding: 15, font: { size: 12 } } },
      title: { display: true, text: title, font: { size: 14, weight: 'normal' }, color: '#6c757d' }
    },
    scales: {
      y: { beginAtZero: true, ticks: { precision: 0 } },
      x: { ticks: { font: { size: 10 }, maxRotation: 45, minRotation: 45 } }
    }
  }
}

// 바 차트 옵션
function getBarChartOptions(title) {
  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top', labels: { usePointStyle: true, padding: 15, font: { size: 12 } } },
      title: { display: true, text: title, font: { size: 14, weight: 'normal' }, color: '#6c757d' }
    },
    scales: {
      y: { beginAtZero: true, ticks: { precision: 0 } },
      x: { ticks: { font: { size: 11 } } }
    }
  }
}

onMounted(async () => {
  emits('set_title', languageStore.get('statisticsManagement'))
  await fetchStatistics()
})

</script>

<template>
  <div class="statisticsArea">
    <!-- Toast Snackbar -->
    <v-snackbar
      v-model="toast.show.value"
      :color="toast.color.value"
      :timeout="toast.timeout.value"
      location="top"
    >
      {{ toast.message.value }}
      <template v-slot:actions>
        <v-btn variant="text" @click="toast.close()">
          {{ languageStore.get('close') }}
        </v-btn>
      </template>
    </v-snackbar>
    <!-- 검색 영역 -->
    <div class="searchArea">
      <div class="searchLeft">
        <div class="statTypeButtons">
          <button
            v-for="type in statTypes"
            :key="type.value"
            :class="['statTypeBtn', { active: selectedStatType === type.value }]"
            @click="selectStatType(type.value)"
          >
            {{ type.label }}
          </button>
        </div>
        <div v-if="selectedStatType === 'DAILY'" class="dateSelector">
          <input
            type="date"
            v-model="selectedDate"
            class="dateInput"
          />
        </div>
        <div v-if="selectedStatType === 'WEEKLY'" class="dateSelector">
          <input
            type="date"
            v-model="selectedWeek"
            class="dateInput"
          />
          <span class="dateHint">{{ languageStore.get('selectMondayHint') }}</span>
        </div>
        <div v-if="selectedStatType === 'MONTHLY'" class="dateSelector">
          <input
            type="month"
            v-model="selectedMonth"
            class="dateInput"
          />
        </div>
      </div>
      <div class="searchRight">
        <button class="searchBtn" @click="fetchStatistics">{{ languageStore.get('search') }}</button>
        <button
          v-if="selectedStatType === 'DAILY'"
          class="generateBtn"
          @click="generateStatistics"
        >{{ languageStore.get('generateStatistics') }}</button>
      </div>
    </div>

    <!-- 메인 컨텐츠 영역 -->
    <div class="mainContentArea">
      <!-- 로딩 -->
      <div v-if="loading" class="loadingArea">
        <div class="spinner"></div>
        <p>{{ languageStore.get('loading') }}</p>
      </div>

      <!-- 통계 데이터 -->
      <div v-else-if="statistics" class="statsContent">
        <!-- 통계 그리드 테이블 -->
        <div class="statsTable">
          <!-- 행1: 회의/방송 관련 -->
          <div class="statsRow">
            <div class="statsCell">
              <div class="statsLabel">{{ languageStore.get('plenarySession') }}</div>
              <div class="statsValue">{{ statistics.plenaryMeetingCount || 0 }}</div>
              <div class="statsUnit">{{ languageStore.get('count') }}</div>
            </div>
            <div class="statsCell">
              <div class="statsLabel">{{ languageStore.get('committee') }}</div>
              <div class="statsValue">{{ statistics.committeeMeetingCount || 0 }}</div>
              <div class="statsUnit">{{ languageStore.get('count') }}</div>
            </div>
            <div class="statsCell">
              <div class="statsLabel">{{ languageStore.get('liveBroadcast') }}</div>
              <div class="statsValue">{{ statistics.liveBroadcastCount || 0 }}</div>
              <div class="statsUnit">{{ languageStore.get('times') }}</div>
            </div>
          </div>
          <!-- 행2: 콘텐츠 배포 관련 -->
          <div class="statsRow">
            <div class="statsCell">
              <div class="statsLabel">{{ languageStore.get('videoUpload') }}</div>
              <div class="statsValue">{{ statistics.videoUploadCount || 0 }}</div>
              <div class="statsUnit">{{ languageStore.get('items') }}</div>
            </div>
            <div class="statsCell">
              <div class="statsLabel">{{ languageStore.get('snsUpload') }}</div>
              <div class="statsValue">{{ statistics.snsUploadCount || 0 }}</div>
              <div class="statsUnit">{{ languageStore.get('count') }}</div>
            </div>
            <div class="statsCell">
              <div class="statsLabel">{{ languageStore.get('ftpTransfer') }}</div>
              <div class="statsValue">{{ statistics.ftpTransferCount || 0 }}</div>
              <div class="statsUnit">{{ languageStore.get('count') }}</div>
            </div>
          </div>
        </div>

        <!-- 차트 영역 -->
        <div class="chartArea">
          <div class="chartTitle">{{ languageStore.get('statisticsTrendRecent30') }}</div>
          <div class="chartContent">
            <canvas ref="chartCanvas"></canvas>
          </div>
        </div>
      </div>

      <div v-else class="noData">
        {{ languageStore.get('noData') }}
      </div>
    </div>
  </div>
</template>

<style scoped>
.statisticsArea {
  width: 70vw;
  max-height: 88vh;
  overflow-x: hidden;
  overflow-y: auto;
  scrollbar-width: none;
  padding-top: 10px;
}

.statisticsArea::-webkit-scrollbar {
  display: none;
}

.searchArea {
  background: white;
  width: 70vw;
  height: 8vh;
  margin: 10px 0;
  padding: 0 20px;
  border-radius: 30px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.searchLeft {
  display: flex;
  align-items: center;
  gap: 15px;
}

.statTypeButtons {
  display: flex;
  gap: 8px;
}

.statTypeBtn {
  padding: 8px 20px;
  border: 1px solid #dee2e6;
  background-color: white;
  color: #495057;
  border-radius: 20px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;
  white-space: nowrap;
}

.statTypeBtn:hover {
  background-color: #e9ecef;
}

.statTypeBtn.active {
  background-color: #052C65;
  color: white;
  border-color: #052C65;
}

.dateSelector {
  margin-left: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.dateInput {
  padding: 8px 12px;
  border: 1px solid #ced4da;
  border-radius: 20px;
  font-size: 14px;
}

.dateHint {
  font-size: 12px;
  color: #6c757d;
  white-space: nowrap;
}

.searchRight {
  display: flex;
  gap: 10px;
}

.searchBtn,
.generateBtn {
  padding: 8px 20px;
  border: none;
  border-radius: 20px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;
  white-space: nowrap;
}

.searchBtn {
  background-color: #052C65;
  color: white;
}

.searchBtn:hover {
  background-color: #041f4a;
}

.generateBtn {
  background-color: #17a2b8;
  color: white;
}

.generateBtn:hover {
  background-color: #138496;
}

.mainContentArea {
  background: white;
  width: 70vw;
  min-height: 70vh;
  border-radius: 5px;
  padding: 30px;
  overflow-y: auto;
}

.loadingArea {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  gap: 15px;
}

.spinner {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #052C65;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.statsContent {
  width: 100%;
}

.statsTable {
  margin-bottom: 30px;
}

.statsRow {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 20px;
}

.statsCell {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 25px;
  text-align: center;
  transition: all 0.2s;
}

.statsCell:hover {
  background: #e9ecef;
  border-color: #052C65;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(5, 44, 101, 0.1);
}

.statsLabel {
  font-size: 14px;
  color: #6c757d;
  margin-bottom: 12px;
  font-weight: 600;
}

.statsValue {
  font-size: 36px;
  font-weight: bold;
  color: #052C65;
  margin: 10px 0;
}

.statsUnit {
  font-size: 13px;
  color: #6c757d;
}

.chartArea {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 20px;
}

.chartTitle {
  font-size: 16px;
  font-weight: bold;
  color: #052C65;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 2px solid #052C65;
}

.chartContent {
  height: 400px;
  position: relative;
  background: white;
  padding: 15px;
  border-radius: 6px;
}

.noData {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #90A4AE;
  padding: 50px;
  font-size: 14px;
  gap: 8px;
}

@media (max-width: 1200px) {
  .statsRow {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .statsRow {
    grid-template-columns: 1fr;
  }
}
</style>
